import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type AppState, type Driver, DriverStatus, SortByField, type User } from '../../types';
import { LIST_MODE_TABLE, PROP_ACTIVE_CARD_NUMBER, SORT_ASC, SORT_DESC } from './schema';
import { ascend, clone, descend, prop, propIs, sortWith } from 'ramda';
import { formatUserNameForDisplay } from './utils';
import { resetSidebar } from './drivers/sidebar/sidebarSlice';
import type { RootState } from '../../configuration/setup/store';
import SortDirection, { type SortDirectionType } from '@rio-cloud/rio-uikit/SortDirection';
import { driversApi } from './services/driversApi';
import { usersApi } from './services/usersApi';

// handle key "cardName" as the fieldname is just "card"
// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const mapSortKey = (sortKey: any) => (sortKey === SortByField.FIELD_CARD ? PROP_ACTIVE_CARD_NUMBER : sortKey);

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const getLowerCaseDriverProp = (sortKey: any, users: User[]) => {
    return (driver: Driver) => {
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        let propValue: any;
        if (sortKey === SortByField.FIELD_USER) {
            const user = users.find(userInternal => userInternal.subject === driver.subject);
            propValue = formatUserNameForDisplay(user);
        } else {
            propValue = prop(sortKey, driver) || '';
        }
        return propIs(String, propValue) ? propValue.toLowerCase() : propValue;
    };
};

const sortDrivers = (
    // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
    sortKey: any,
    drivers: Driver[] = [],
    users: User[] = [],
    sortDir: SortDirectionType = SortDirection.ASCENDING
) => {
    const sortDirFunc =
        sortDir === SortDirection.ASCENDING
            ? ascend(getLowerCaseDriverProp(sortKey, users))
            : descend(getLowerCaseDriverProp(sortKey, users));
    return sortWith([sortDirFunc])(drivers);
};

// biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
const determineSortDir = (oldSortBy: any, oldSortDir: any, sortBy: any) => {
    if (oldSortBy) {
        const invertedSortDir = oldSortDir === SORT_ASC ? SORT_DESC : SORT_ASC;
        return oldSortBy === sortBy ? invertedSortDir : oldSortDir;
    }
    return SORT_ASC;
};

export const initialState: AppState = {
    drivers: [],
    driverCreation: {
        inProgress: false,
        showCreationDialog: false,
    },
    driversListMode: LIST_MODE_TABLE,
    searchText: '',
    sortDir: SortDirection.ASCENDING,
    sortBy: SortByField.FIELD_LAST_NAME,
    driverStatuses: [DriverStatus.ACTIVE],
    fetchDriversRequested: false,
    hasFetchedDriversOnce: false,
    hasFetchError: false,
    users: [],
    inviteUserRequested: false,
    selectedDriverId: null,
    sessionWarningAcknowledged: false,
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        // TOGGLE_DRIVER_CREATION_DIALOG
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        toggleDriverCreationDialog: (state, action: PayloadAction<any>) => {
            state.driverCreation.showCreationDialog = action.payload;
        },
        // DRIVER_CREATION_STARTED
        driverCreationStarted: state => {
            state.driverCreation.inProgress = true;
        },
        // DRIVER_CREATION_FINISHED
        driverCreationFinished: state => {
            state.driverCreation.inProgress = false;
            state.driverCreation.showCreationDialog = false;
        },
        // DRIVER_DESELECTED
        driverDeselected: state => {
            state.selectedDriverId = null;
        },
        // DRIVER_SEARCH_TEXT_CHANGED
        searchTextChanged: (state, action: PayloadAction<string>) => {
            state.searchText = action.payload;
        },
        // SORT_CHANGED
        sortChanged: (state, action: PayloadAction<{ sortBy: string; sortDir?: SortDirectionType }>) => {
            const sort = action.payload;
            const sortKey = mapSortKey(sort.sortBy);
            const sortDir = sort.sortDir ? sort.sortDir : determineSortDir(state.sortBy, state.sortDir, sort.sortBy);

            state.drivers = sortDrivers(sortKey, clone(state.drivers), state.users, sortDir);
            state.sortBy = sort.sortBy;
            state.sortDir = sortDir;
        },
        // DRIVER_STATUSES_CHANGED
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        driverStatusesChanged: (state, action: PayloadAction<any>) => {
            state.driverStatuses = action.payload;
        },
        // DRIVERS_FETCH_REQUESTED
        fetchDriversRequested: state => {
            state.fetchDriversRequested = true;
        },
        // DRIVERS_FETCH_FAILED
        fetchDriversFailed: state => {
            state.fetchDriversRequested = false;
            state.hasFetchedDriversOnce = true;
            state.hasFetchError = false;
        },
        // DRIVERS_FETCHED
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        driversFetched: (state, action: PayloadAction<any>) => {
            const fetchedDriverList = action.payload || [];
            state.fetchDriversRequested = false;
            state.hasFetchedDriversOnce = true;
            state.hasFetchError = false;
            state.drivers = sortDrivers(mapSortKey(state.sortBy), fetchedDriverList, state.users, state.sortDir);
        },
        // DRIVERS_LIST_MODE_CHANGED
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        driversListModeChanged: (state, action: PayloadAction<any>) => {
            state.driversListMode = action.payload;
        },
        // SESSION_WARNING_DIALOG_CLOSED
        hideSessionWarning: state => {
            state.sessionWarningAcknowledged = true;
        },
        // USERS_FETCHED
        // biome-ignore lint/suspicious/noExplicitAny: suppress out of convenience during migration to biome
        usersFetched: (state, action: PayloadAction<any>) => {
            const users = action.payload || [];
            if (state.sortBy === SortByField.FIELD_USER) {
                state.users = users;
                state.drivers = sortDrivers(mapSortKey(state.sortBy), state.drivers, users, state.sortDir);
            } else {
                state.users = users;
            }
        },
        // INVITE_USER_FAILED
        inviteUserFailed: state => {
            state.inviteUserRequested = true;
        },
        // INVITE_USER_SUCCESS
        inviteUserSuccess: state => {
            state.inviteUserRequested = false;
        },
        // INVITE_USER_REQUESTED
        inviteUserRequested: state => {
            state.inviteUserRequested = true;
        },

        // ----------------------------------------
        // FROM CROSS SLICE REDUCER
        // ----------------------------------------
        // DRIVER_SELECTED
        driverSelected: (state, action: PayloadAction<string | null>) => {
            state.selectedDriverId = action.payload;
        },
    },
    extraReducers: builder => {
        // RESET_SIDEBAR
        builder.addCase(resetSidebar, state => {
            state.selectedDriverId = null;
        });
        builder.addDefaultCase(() => {});
    },
});

export const {
    toggleDriverCreationDialog,
    driverCreationFinished,
    driverCreationStarted,
    searchTextChanged,
    driversFetched,
    fetchDriversFailed,
    fetchDriversRequested,
    driverStatusesChanged,
    driversListModeChanged,
    hideSessionWarning,
    inviteUserFailed,
    inviteUserSuccess,
    inviteUserRequested,
    usersFetched,
    sortChanged,
    driverSelected,
    driverDeselected,
} = appSlice.actions;

// SELECTORS
export const getUsersRtk = (state: RootState): User[] =>
    usersApi.endpoints.getUsers.select()(state).data ?? state.app.users;
const getDriversQueryData = (state: RootState) => driversApi.endpoints.getDrivers.select()(state).data;
const getStoredDrivers = (state: RootState) => state.app.drivers;
const getStoredSortBy = (state: RootState) => state.app.sortBy;
const getStoredSortDir = (state: RootState) => state.app.sortDir;
export const getDriversRtk = createSelector(
    [getDriversQueryData, getStoredDrivers, getStoredSortBy, getStoredSortDir, getUsersRtk],
    (queryDrivers, storedDrivers, sortBy, sortDir, users) => {
        if (!queryDrivers) {
            return storedDrivers;
        }

        return sortDrivers(mapSortKey(sortBy), clone(queryDrivers), users, sortDir);
    }
);
export const getDriverCreationInProgressRtk = (state: RootState) => state.app.driverCreation.inProgress;
export const getShowDriverCreationDialogRtk = (state: RootState) => state.app.driverCreation.showCreationDialog;
export const getDriverStatusesRtk = (state: RootState): DriverStatus[] => state.app.driverStatuses;
export const getDriversListModeRtk = (state: RootState) => state.app.driversListMode;
export const hasFetchDriversRequestedRtk = (state: RootState) => {
    const query = driversApi.endpoints.getDrivers.select()(state);
    return query.isUninitialized ? state.app.fetchDriversRequested : query.isLoading;
};
export const hasFetchedDriversOnceRtk = (state: RootState) => {
    const query = driversApi.endpoints.getDrivers.select()(state);
    return query.isUninitialized
        ? state.app.hasFetchedDriversOnce || state.app.drivers.length > 0
        : query.isSuccess || query.isError;
};
export const hasFetchErrorRtk = (state: RootState) => {
    const query = driversApi.endpoints.getDrivers.select()(state);
    return query.isUninitialized ? state.app.hasFetchError : query.isError;
};
export const getSearchTextRtk = (state: RootState) => state.app.searchText;
export const getSortByRtk = (state: RootState) => state.app.sortBy;
export const getSortDirRtk = (state: RootState) => state.app.sortDir;
export const isInviteInProgressRtk = (state: RootState) => state.app.inviteUserRequested;
export const getSelectedDriverIdRtk = (state: RootState) => state.app.selectedDriverId;
export const getSessionWarningAcknowledgedRtk = (state: RootState) => state.app.sessionWarningAcknowledged;

export default appSlice.reducer;
